import React from "react";
import {
  Button,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular, Sparkle24Regular } from "@fluentui/react-icons";
import { formatTime, type DashboardData } from "../dashboard";

interface DashboardPanelProps {
  data: DashboardData;
  loading: boolean;
  summary: string | null;
  summaryState: "idle" | "loading" | "error";
  onSummarize: () => void;
  onClose: () => void;
}

const sectionStyle: React.CSSProperties = {
  padding: "12px",
  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
};

export function DashboardPanel({
  data,
  loading,
  summary,
  summaryState,
  onSummarize,
  onClose,
}: DashboardPanelProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", fontFamily: "Segoe UI, sans-serif" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
          flexShrink: 0,
        }}
      >
        <Text weight="semibold">Dashboard</Text>
        <Button appearance="subtle" size="small" icon={<Dismiss24Regular />} aria-label="Close" onClick={onClose} />
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spinner label="Loading dashboard…" />
          </div>
        ) : (
          <>
            {/* Unread */}
            <div style={sectionStyle}>
              <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>
                Unread
              </Text>
              <div>
                <Text size={800} weight="bold">
                  {data.unreadCount}
                </Text>
              </div>
            </div>

            {/* Needs reply */}
            <div style={sectionStyle}>
              <Text weight="semibold">Needs reply</Text>
              {data.needsReply.length === 0 ? (
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    Nothing awaiting a reply.
                  </Text>
                </div>
              ) : (
                data.needsReply.map((e) => (
                  <div key={e.id} style={{ marginTop: 6 }}>
                    <Text size={300} weight="semibold">
                      {e.subject}
                    </Text>
                    <div>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {e.from}
                      </Text>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Today's meetings */}
            <div style={sectionStyle}>
              <Text weight="semibold">Today's meetings</Text>
              {data.meetingsUnavailable ? (
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    Calendar requires Graph (Exchange Online).
                  </Text>
                </div>
              ) : data.meetings.length === 0 ? (
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    No meetings today.
                  </Text>
                </div>
              ) : (
                data.meetings.map((m) => (
                  <div key={m.id} style={{ marginTop: 6 }}>
                    <Text size={300}>
                      {formatTime(m.start)} · {m.subject}
                    </Text>
                  </div>
                ))
              )}
            </div>

            {/* Categories */}
            <div style={sectionStyle}>
              <Text weight="semibold">Categories</Text>
              {data.categories.length === 0 ? (
                <div>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    No categories on recent mail.
                  </Text>
                </div>
              ) : (
                data.categories.map((c) => (
                  <div
                    key={c.name}
                    style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}
                  >
                    <Text size={300}>{c.name}</Text>
                    <Text size={300} weight="semibold">
                      {c.count}
                    </Text>
                  </div>
                ))
              )}
            </div>

            {/* AI summary */}
            <div style={{ padding: 12 }}>
              <Button
                appearance="primary"
                icon={<Sparkle24Regular />}
                onClick={onSummarize}
                disabled={summaryState === "loading"}
              >
                Summarize my day
              </Button>
              {summaryState === "loading" && (
                <div style={{ marginTop: 8 }}>
                  <Spinner size="tiny" label="Summarizing…" />
                </div>
              )}
              {summaryState === "error" && (
                <div style={{ marginTop: 8 }}>
                  <MessageBar intent="error">
                    <MessageBarBody>Couldn't generate a summary. Please try again.</MessageBarBody>
                  </MessageBar>
                </div>
              )}
              {summary && summaryState !== "error" && (
                <div style={{ marginTop: 8 }}>
                  <Text size={300}>{summary}</Text>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
